// Built on React Aria's toast hooks rather than its UNSTABLE_Toast components,
// which unmount the region at zero toasts, so the last toast couldn't animate
// out. The hooks are stable and carry the accessibility and layering: the
// region sets `data-react-aria-top-layer`, which keeps it out of the inert
// subtree React Aria applies around an open modal, so a toast stays clickable
// over one.
import {
  animated,
  easings,
  to,
  useReducedMotion,
  useTransition,
} from "@react-spring/web";
import { IconCircleCheck, IconExclamationCircle } from "@tabler/icons-react";
import {
  type FC,
  type ReactNode,
  type Ref,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  mergeProps,
  type ToastAria,
  useFocusRing,
  useToast,
  useToastRegion,
} from "react-aria";
import { createPortal } from "react-dom";
import {
  type QueuedToast,
  ToastQueue,
  type ToastState,
  useToastQueue,
} from "react-stately";
import { twMerge } from "tailwind-merge";
import { ModalCloseButton } from "../Modal/modalCloseButton.js";

export const ToastVariant = {
  Success: "SUCCESS",
  Error: "ERROR",
} as const;
export type ToastVariant = (typeof ToastVariant)[keyof typeof ToastVariant];

export interface ToastContent {
  title: string;
  description?: string;
  variant: ToastVariant;
}

/** React Aria's minimum timeout for an auto-dismissing toast. */
const SUCCESS_TIMEOUT_MS = 5000;

/** The toasts shown in full; older ones wait in a stack behind the last. */
const MAX_VISIBLE_TOASTS = 3;
/** Stacked layers shown; deeper ones sit invisibly in the last. */
const MAX_STACKED_LAYERS = 2;
/** How far each stacked layer peeks out below the one in front of it. */
const STACK_PEEK_PX = 8;

// Uncapped: its own cap hides the overflow, which we stack instead.
const toastQueue = new ToastQueue<ToastContent>();

/** Shows a toast; a success dismisses itself, an error stays until closed. */
export function showToast(content: ToastContent): void {
  toastQueue.add(content, {
    timeout:
      content.variant === ToastVariant.Success ? SUCCESS_TIMEOUT_MS : undefined,
  });
}

/**
 * The front toasts as React Aria's hooks should see them. Only these are live:
 * `useToast` runs a toast's timer while mounted, so a stacked toast waits with
 * a paused timer and gets the full timeout once it comes forward. The region's
 * hover and focus pause and resume every visible toast, which would start the
 * stacked timers, and its focus handling indexes the visible toasts against
 * the rendered toast roles, so it gets this view too.
 */
function buildFrontState(
  visibleToasts: readonly QueuedToast<ToastContent>[],
): ToastState<ToastContent> {
  const frontToasts = visibleToasts.slice(0, MAX_VISIBLE_TOASTS);
  return {
    visibleToasts: frontToasts,
    add: (content, options) => toastQueue.add(content, options),
    close: (key) => {
      toastQueue.close(key);
    },
    pauseAll: () => {
      for (const toast of frontToasts) {
        toast.timer?.pause();
      }
    },
    resumeAll: () => {
      for (const toast of frontToasts) {
        toast.timer?.resume();
      }
    },
  };
}

interface ToastSprings {
  rows: number;
  opacity: number;
  x: number;
  y: number;
  scale: number;
  /** 1 clips a stacked card to its bottom edge. */
  clip: number;
}

const FRONT_SPRINGS: ToastSprings = {
  rows: 1,
  opacity: 1,
  x: 0,
  y: 0,
  scale: 1,
  clip: 0,
};

/** A closed card first slides out and fades over this long. */
const LEAVE_FADE_MS = 150;
/** Then its slot collapses. */
const LEAVE_COLLAPSE_CONFIG = {
  duration: 150,
  easing: easings.easeInOutCubic,
};

/** Where a toast rests at its position in the queue, newest first. */
function computeToastSprings(depth: number): ToastSprings {
  if (depth < MAX_VISIBLE_TOASTS) {
    return FRONT_SPRINGS;
  }
  const stackedDepth = depth - MAX_VISIBLE_TOASTS;
  const layer = Math.min(stackedDepth + 1, MAX_STACKED_LAYERS);
  return {
    rows: 0,
    opacity: stackedDepth < MAX_STACKED_LAYERS ? 1 : 0,
    x: 0,
    y: layer * STACK_PEEK_PX,
    scale: 1 - layer * 0.05,
    clip: 1,
  };
}

/** Renders the queued toasts at the top right; mount once per app. */
export const ToastRegion: FC = () => {
  const state = useToastQueue(toastQueue);
  const frontState = useMemo(
    () => buildFrontState(state.visibleToasts),
    [state.visibleToasts],
  );
  // Skips every spring, not only these, while the user prefers reduced motion.
  useReducedMotion();

  // Stays mounted after the last toast closes, until it has animated out.
  const [isRegionMounted, setIsRegionMounted] = useState(false);
  if (state.visibleToasts.length > 0 && !isRegionMounted) {
    setIsRegionMounted(true);
  }

  const findDepth = (toast: QueuedToast<ToastContent>) =>
    state.visibleToasts.findIndex(
      (visibleToast) => visibleToast.key === toast.key,
    );

  const transitions = useTransition(state.visibleToasts, {
    keys: (toast: QueuedToast<ToastContent>) => toast.key,
    from: { ...FRONT_SPRINGS, rows: 0, opacity: 0, y: -8 },
    enter: (toast: QueuedToast<ToastContent>) => ({
      ...computeToastSprings(findDepth(toast)),
      config: { duration: 250, easing: easings.easeOutCubic },
    }),
    update: (toast: QueuedToast<ToastContent>) => {
      const depth = findDepth(toast);
      if (depth < MAX_VISIBLE_TOASTS) {
        // A toast coming forward zooms in where it is. Its slot grows exactly
        // as the closed card's collapses, so the slots' total height, and
        // with it the card, holds still while the cards above move up.
        return {
          ...FRONT_SPRINGS,
          delay: (key: string) => (key === "rows" ? LEAVE_FADE_MS : 0),
          config: (key: string) =>
            key === "rows"
              ? LEAVE_COLLAPSE_CONFIG
              : { duration: 250, easing: easings.easeOutCubic },
        };
      }
      // A toast pushed into the stack first slides down with the others while
      // the new one enters.
      return {
        ...computeToastSprings(depth),
        delay: 250,
        config: { duration: 250, easing: easings.easeInOutCubic },
      };
    },
    // The card slides out, then its now invisible slot collapses. A per-key
    // delay rather than a second keyframe keeps the collapse on the same
    // frame as a promoted toast's growing slot.
    leave: {
      opacity: 0,
      x: 100,
      rows: 0,
      delay: (key: string) => (key === "rows" ? LEAVE_FADE_MS : 0),
      config: (key: string) =>
        key === "rows"
          ? LEAVE_COLLAPSE_CONFIG
          : { duration: LEAVE_FADE_MS, easing: easings.easeInCubic },
    },
    onDestroyed: () => {
      if (state.visibleToasts.length === 0) {
        setIsRegionMounted(false);
      }
    },
  });

  if (!isRegionMounted) {
    return null;
  }

  return (
    <ToastRegionElement state={frontState}>
      {transitions((style, toast, _transition, index) => {
        const isLive = frontState.visibleToasts.some(
          (frontToast) => frontToast.key === toast.key,
        );
        return (
          // The slot's single grid row grows from 0fr to 1fr and back, so the
          // other toasts slide to make room or close the gap. The card is
          // pinned to the slot's bottom: an entering card comes down from
          // above the top edge, and at 0fr a stacked card lines up with the
          // bottom of the card before it. Below 1fr the row fills only that
          // fraction of the slot, so content-end keeps it at the bottom too.
          // Newer slots paint above older ones, against DOM order, so the
          // stack sits behind the front cards.
          <animated.div
            className="relative grid content-end"
            style={{
              zIndex: -index,
              gridTemplateRows: style.rows.to((rows) => `${rows}fr`),
            }}
          >
            <div className="flex min-h-0 flex-col justify-end">
              {/* The spacing between toasts is this bottom margin, inside the
                  collapsing slot, so removing a collapsed slot doesn't jump. */}
              <animated.div
                className="mb-4 origin-bottom"
                style={{
                  opacity: style.opacity,
                  transform: to(
                    [style.x, style.y, style.scale],
                    (x, y, scale) => `translate(${x}%, ${y}px) scale(${scale})`,
                  ),
                  // A stacked card keeps only its bottom padding, so neither
                  // a taller card nor one pushed down while the card in front
                  // collapses shows its content. The outset spares the shadow,
                  // ring and focus outline.
                  clipPath: style.clip.to(
                    (clip) =>
                      `inset(calc((100% - 1rem) * ${clip} - 1.5rem * ${1 - clip}) -1.5rem -1.5rem)`,
                  ),
                }}
              >
                {isLive ? (
                  <Toast toast={toast} state={frontState} />
                ) : (
                  <ToastCard content={toast.content} />
                )}
              </animated.div>
            </div>
          </animated.div>
        );
      })}
    </ToastRegionElement>
  );
};

interface ToastRegionElementProps {
  state: ToastState<ToastContent>;
  children: ReactNode;
}

/** Mounted only while there are toasts, so the landmark registers its element. */
const ToastRegionElement: FC<ToastRegionElementProps> = (props) => {
  const { state, children } = props;
  const ref = useRef<HTMLDivElement>(null);
  const { regionProps } = useToastRegion({}, state, ref);

  return createPortal(
    // z-50 sits above the modal overlay's z-10. Newest first in the queue, so
    // the newest toast is nearest the corner.
    <div
      {...regionProps}
      ref={ref}
      className="pointer-events-none fixed top-0 right-0 z-50 flex w-full max-w-sm flex-col px-6 pt-6 pb-2"
    >
      {children}
    </div>,
    document.body,
  );
};

interface ToastProps {
  toast: QueuedToast<ToastContent>;
  state: ToastState<ToastContent>;
}

const Toast: FC<ToastProps> = (props) => {
  const { toast, state } = props;
  const ref = useRef<HTMLDivElement>(null);
  const toastAria = useToast({ toast }, state, ref);
  const { focusProps, isFocusVisible } = useFocusRing();

  return (
    <ToastCard
      ref={ref}
      content={toast.content}
      aria={{
        ...toastAria,
        toastProps: mergeProps(toastAria.toastProps, focusProps),
      }}
      isFocusVisible={isFocusVisible}
    />
  );
};

interface ToastCardProps {
  ref?: Ref<HTMLDivElement>;
  content: ToastContent;
  /**
   * Absent while the toast is stacked or animates out: without the toast
   * roles, the region's focus handling, which finds toasts by role and matches
   * them to the front toasts by index, can't land on it.
   */
  aria?: ToastAria;
  isFocusVisible?: boolean;
}

const ToastCard: FC<ToastCardProps> = (props) => {
  const { ref, content, aria, isFocusVisible = false } = props;

  return (
    <div
      {...aria?.toastProps}
      ref={ref}
      inert={!aria}
      className={twMerge(
        "pointer-events-auto w-full overflow-hidden rounded-lg bg-white p-4 shadow-lg ring-1 ring-black/5",
        isFocusVisible
          ? "outline-2 outline-offset-2 outline-primary"
          : "outline-hidden",
        !aria && "pointer-events-none",
      )}
    >
      <div className="flex items-start gap-3">
        <div className="shrink-0">{renderIcon(content.variant)}</div>
        <div {...aria?.contentProps} className="w-0 flex-1 pt-0.5">
          <div
            {...aria?.titleProps}
            className="text-sm font-medium text-foreground"
          >
            {content.title}
          </div>
          {content.description ? (
            <div
              {...aria?.descriptionProps}
              className="mt-1 text-sm text-muted-foreground"
            >
              {content.description}
            </div>
          ) : null}
        </div>
        <ModalCloseButton onPress={aria?.closeButtonProps.onPress} />
      </div>
    </div>
  );
};

function renderIcon(variant: ToastVariant) {
  switch (variant) {
    case ToastVariant.Success:
      return (
        <IconCircleCheck className="size-6 text-green-400" aria-hidden="true" />
      );
    case ToastVariant.Error:
      return (
        <IconExclamationCircle
          className="size-6 text-red-400"
          aria-hidden="true"
        />
      );
  }
}
